import { useMemo } from 'react';
import { Loader2, TrendingUp, TrendingDown, Minus, Battery, Sun, Zap } from 'lucide-react';
import { useEnergyTimeSeries } from '@/hooks/useEnergyTimeSeries';
import type { CompareSlot, TimeGranularity, GroupTimeSeriesPoint } from '@/types/reports';
import type { Group, IndustryType } from '@/types';

interface CompareStatsSectionProps {
  slot: CompareSlot;
  dateRange: { start: Date | null; end: Date | null } | null;
  granularity: TimeGranularity;
  groups: Group[];
  selectedIndustryTypes: IndustryType[];
}

export function CompareStatsSection({
  slot,
  dateRange,
  granularity,
  groups: _groups,
  selectedIndustryTypes,
}: CompareStatsSectionProps) {
  // Fetch time series data for this slot
  const { timeSeries: timeSeriesData, isLoading } = useEnergyTimeSeries({
    groupIds: slot.groupIds,
    startDate: dateRange?.start || null,
    endDate: dateRange?.end || null,
    granularity,
    modelCodes: slot.modelCodes,
    industryTypes: selectedIndustryTypes,
    enabled: slot.groupIds.length > 0 && !!dateRange,
  });

  // Calculate statistics from time series data
  const stats = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) {
      return null;
    }

    const solarValues = timeSeriesData.map((p: GroupTimeSeriesPoint) => p.solarKwh || 0);
    const gridValues = timeSeriesData.map((p: GroupTimeSeriesPoint) => p.gridKwh || 0);
    const totalValues = timeSeriesData.map((p: GroupTimeSeriesPoint) => (p.solarKwh || 0) + (p.gridKwh || 0));

    const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
    const avg = (arr: number[]) => arr.length > 0 ? sum(arr) / arr.length : 0;
    const max = (arr: number[]) => arr.length > 0 ? Math.max(...arr) : 0;
    const min = (arr: number[]) => arr.length > 0 ? Math.min(...arr) : 0;

    // Calculate trend (compare first half to second half)
    const midpoint = Math.floor(solarValues.length / 2);
    const firstHalf = solarValues.slice(0, midpoint);
    const secondHalf = solarValues.slice(midpoint);
    const firstHalfAvg = avg(firstHalf);
    const secondHalfAvg = avg(secondHalf);
    const trend = firstHalfAvg > 0
      ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100
      : 0;

    return {
      totalSolar: sum(solarValues),
      totalGrid: sum(gridValues),
      totalConsumption: sum(totalValues),
      avgSolar: avg(solarValues),
      avgGrid: avg(gridValues),
      peakSolar: max(solarValues),
      minSolar: min(solarValues),
      periods: timeSeriesData.length,
      trend,
    };
  }, [timeSeriesData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[180px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-full min-h-[180px] text-sm text-muted-foreground">
        No data available for selected period
      </div>
    );
  }

  const TrendIcon = stats.trend > 5 ? TrendingUp : stats.trend < -5 ? TrendingDown : Minus;
  const trendColor = stats.trend > 5 ? 'text-green-500' : stats.trend < -5 ? 'text-red-500' : 'text-muted-foreground';

  const offGridPercent = stats.totalConsumption > 0
    ? (stats.totalSolar / stats.totalConsumption) * 100
    : 0;

  return (
    <div className="space-y-4">
      {/* Summary Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <Sun className="h-5 w-5 mx-auto mb-1.5" style={{ color: slot.color }} />
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Solar</div>
          <div className="text-base font-semibold mt-1">
            {stats.totalSolar.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="text-xs font-normal text-muted-foreground ml-1">kWh</span>
          </div>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <Zap className="h-5 w-5 mx-auto mb-1.5 text-yellow-500" />
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Grid</div>
          <div className="text-base font-semibold mt-1">
            {stats.totalGrid.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="text-xs font-normal text-muted-foreground ml-1">kWh</span>
          </div>
        </div>
        <div className="bg-muted/30 rounded-lg p-3 text-center">
          <Battery className="h-5 w-5 mx-auto mb-1.5 text-blue-500" />
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
          <div className="text-base font-semibold mt-1">
            {stats.totalConsumption.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            <span className="text-xs font-normal text-muted-foreground ml-1">kWh</span>
          </div>
        </div>
      </div>

      {/* Off-Grid Percentage Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground uppercase tracking-wider">Off-Grid Usage</span>
          <span className="font-medium" style={{ color: slot.color }}>{offGridPercent.toFixed(1)}%</span>
        </div>
        <div className="h-3 bg-muted/50 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${offGridPercent}%`,
              backgroundColor: slot.color
            }}
          />
        </div>
      </div>

      {/* Detail Stats */}
      <div className="space-y-2 text-xs">
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Avg Solar/{granularity.slice(0, -2)}</span>
          <span className="font-medium">{stats.avgSolar.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </div>
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Peak Solar</span>
          <span className="font-medium">{stats.peakSolar.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </div>
        <div className="flex justify-between p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Min Solar</span>
          <span className="font-medium">{stats.minSolar.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</span>
        </div>
        <div className="flex justify-between items-center p-2 bg-muted/20 rounded">
          <span className="text-muted-foreground">Trend</span>
          <span className={`font-medium flex items-center gap-1 ${trendColor}`}>
            <TrendIcon className="h-3.5 w-3.5" />
            {Math.abs(stats.trend).toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Periods info */}
      <div className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
        Based on {stats.periods} {granularity.slice(0, -2)} periods
      </div>
    </div>
  );
}
